// CTSK-264 — Read-time validation UI (Knowledge-Data Platform,
// CTSK-255 read-time slice). Shared warning-row wrapper for the five
// registry-file table renderers: rows whose entry fails
// validateRegistryEntry() render through this component instead of a
// plain <tr>, picking up a subtle yellow tint + left accent
// (`--warning-primary`, light/dark aware) and a hover tooltip listing
// every issue path-prefixed. Cells still render whatever data IS
// present — surfacing drift, never hiding it (fixing is CTSK-256).
//
// Accepts all standard <tr> props (className merges, title is owned
// by the tooltip) so callers with rich rows — DataSourcesTable's
// clickable expand row — can route through unchanged.

import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import type { RegistryEntryIssue } from '@captain-tasko/shared';

interface WarningRowProps extends ComponentPropsWithoutRef<'tr'> {
  issues: RegistryEntryIssue[];
  /** The row's <td> cells, still populated with whatever data is present. */
  children: ReactNode;
}

/** Build the hover-tooltip text: one line per issue, path-prefixed
 *  ('(root)' for entry-level issues like unrecognized_keys on a
 *  non-object). Exported for the table tests. */
export function warningTooltip(issues: RegistryEntryIssue[]): string {
  return (
    'Validation issues:\n' +
    issues
      .map((iss) => `- ${iss.path.join('.') || '(root)'}: ${iss.message}`)
      .join('\n')
  );
}

export function WarningRow({ issues, children, className, title: _title, ...rest }: WarningRowProps) {
  return (
    <tr
      {...rest}
      className={['registry-warning-row', className].filter(Boolean).join(' ')}
      title={warningTooltip(issues)}
    >
      {children}
    </tr>
  );
}
